import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

interface EditableDialogProps {
  title: string;
  message: string;
  initialValue: string | undefined;
  close: () => void;
  save: (newValue: string) => void;
}

/**
 * EditableDialog is a React functional component that renders a dialog
 * with editable content. It allows users to input or modify text and
 * save or cancel their changes.
 *
 * @param {EditableDialogProps} props - The props for the component.
 * @param {string} props.title - The title displayed at the top of the dialog.
 * @param {string} props.message - A message or description displayed above the input field.
 * @param {string} [props.initialValue] - The initial value of the editable text field.
 * @param {() => void} props.close - A callback function to close the dialog.
 * @param {(value: string) => void} props.save - A callback function to save the edited value.
 */
const EditableDialog: React.FC<EditableDialogProps> = ({
  title,
  message,
  initialValue,
  close,
  save,
}) => {
  const [value, setValue] = useState(initialValue ?? '');

  return (
    <Dialog open fullWidth maxWidth="md" onClose={close} onClick={(e) => e.stopPropagation()}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          {message}
        </Typography>
        <TextField
          value={value}
          onChange={(e) => setValue(e.target.value)}
          variant="outlined"
          multiline
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={close}>
          Cancel
        </Button>
        <Button onClick={() => save(value)} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditableDialog;
